/**
 * LotSmith — Shared dashboard data context.
 *
 * Both DevProvider (demo + ?state= previews) and LiveProvider (M12 — real
 * D1 reads via worker-license) expose this exact shape so DashboardShell
 * and route components don't care which one is mounted.
 */
import { createContext, useContext } from "react";

import type { DemoTrade, EquityPoint } from "../data/demo.js";

export type EaState =
  | "NORMAL"
  | "BLOCKED"
  | "TARGET_HIT"
  | "COOLDOWN"
  | "OVER_LIMIT"
  | "ALGO_OFF";

export interface DevAccount {
  id: string;
  propFirm: string;
  phase: string;
  brokerLogin: string;
  currency: string;
  startBalance: number;
  currentEquity: number;
  eaState: EaState | null;
  eaStateReason: string | null;
  eaStateUpdatedAt: string | null;
}

export interface DevMetrics {
  equity: number;
  equityDelta: number;
  equityDeltaPct: number;
  dailyPnl: number;
  tradesToday: number;
  winRate: number;
  dailyLossLeft: number;
  dailyLossUsedPct: number;
  totalLossLeft: number;
  totalLossUsedPct: number;
  highWaterEquity: number;
  trailingDrawdown: number;
  trailingDrawdownLeft: number;
  trailingDrawdownUsedPct: number;
  toTarget: number;
  targetProgressPct: number;
  minTradingDaysDone: number;
  minTradingDaysRequired: number;
  minTradingDaysProgressPct: number;
  consistencyUsedPct: number;
  consistencyLimitPct: number;
  revengeTradesToday: number;
  revengeWindowMinutes: number;
  disciplineInsights: DisciplineInsight[];
  rules?: {
    propFirm: string;
    dailyLossPct: number;
    totalLossPct: number;
    targetPct: number;
    minTradingDays: number;
    consistencyMaxDayPct: number;
    resetTimezone: string;
    newsPolicy?: "allowed" | "discouraged" | "blackout";
    newsBlackoutMinutes?: number;
    newsNotes?: string;
  };
}

export interface DisciplineInsight {
  kind: "time_of_day" | "symbol_bias" | "mistake_tag";
  label: string;
  detail: string;
  severity: "info" | "warning";
  value: number;
}

export interface DevUser {
  email: string;
  tier: "FREE" | "PRO" | "TRADER" | "TEAM";
}

export type DataState = "loading" | "error" | "empty" | "new-free" | "pro-full";
export type EaStatus = "connected" | "idle" | "offline" | "never-installed";

export interface DevCtx {
  isLoading: boolean;
  error: string | null;
  user: DevUser;
  accounts: DevAccount[];
  activeAccountId: string;
  setActiveAccountId: (id: string) => void;
  activeAccount: DevAccount | undefined;
  metrics: DevMetrics | null;
  equity: EquityPoint[];
  trades: DemoTrade[];
  eaStatus: EaStatus;
  lastSyncAt: Date | null;
  reload: () => void;
  /**
   * Save (or clear with null) the user-editable journal note on a trade.
   * Optimistically updates the in-memory trade list; rolls back on failure.
   */
  updateTradeNote: (tradeId: string, note: string | null) => Promise<void>;

  /**
   * Save the structured journal fields (mood + setup/mistake tags). Each
   * argument is independently optional; pass undefined to leave that
   * column alone, null to clear it. Optimistic + rollback like
   * updateTradeNote.
   */
  updateTradeJournal: (tradeId: string, patch: TradeJournalPatch) => Promise<void>;
}

export interface TradeJournalPatch {
  mood?: import("../data/demo.js").Mood | null;
  setupTags?: string[] | null;
  mistakeTags?: string[] | null;
}

export const DataCtx = createContext<DevCtx | null>(null);

export function useDev(): DevCtx {
  const v = useContext(DataCtx);
  if (!v) throw new Error("useDev must be inside a data provider");
  return v;
}
